import { action } from 'mobx';
import { Observer } from 'mobx-react-lite';
import React from 'react';
import AppPage from '../../base/components/AppPage/AppPage';
import AppPageContent from '../../base/components/AppPageContent/AppPageContent';
import AppPageContentFooter from '../../base/components/AppPageContentFooter/AppPageContentFooter';
import AppPageHeader from '../../base/components/AppPageHeader/AppPageHeader';
import BaseButton from '../../base/components/BaseButton/BaseButton';
import OverlayCloseButton from '../../base/components/OverlayCloseButton/OverlayCloseButton';
import ShadedBlock from '../../base/components/ShadedBlock/ShadedBlock';
import UIBlock from '../../base/components/UIBlock/UIBlock';
import { useOnMount } from '../../base/hooks/lifecycle.hooks';
import { useControllers } from '../../base/hooks/useRootController.hook';
import { useProps, useStore } from '../../base/utils/mobx.utils';
import { getNowTimestampUtc } from '../../base/utils/time.utils';
import { ModelName } from '../../constants/modelNames.enum';
import { AssignmentGAD7 } from '../../models/makeAssignment.model';
import { makeSurveyGAD7, SurveyGAD7 } from '../../models/makeSurveyGAD7.model';
import { makeSubmitSurveyFn } from '../../requests/makeSubmitSurveyFn.request';
import { getAssignmentAssociatedModelTypeDisplayName, getAssignmentTargetModelTypeDisplayName } from '../../utils/assignment.utils';
import SurveyEditorGAD7 from '../SurveyEditorGAD7/SurveyEditorGAD7';
// import './OverlaySurveyGad7.scss';

type OverlaySurveyGAD7Props = {
	assignment?: AssignmentGAD7,
	assignmentId?: string,
	survey?: SurveyGAD7,
	onComplete?: (a?: AssignmentGAD7) => unknown,
	readonly?: any,
	onCloseButton?: () => void;
	hideCloseButton?: boolean,
}

const OverlaySurveyGAD7: React.FC<OverlaySurveyGAD7Props> = props => {

	const p = useProps(props);

	const { API, UI, AUTH } = useControllers();

	const s = useStore(() => ({
		_assignment: undefined as AssignmentGAD7 | undefined,
		get assignment() {
			return p.assignment || s._assignment;
		},
		get assignmentId() {
			return s.assignment?.id || p.assignmentId;
		},
		survey: p.survey ?? makeSurveyGAD7(p.assignment?.target),
		get formIsComplete() {
			return Object.entries(s.survey.$snapshot)
				.filter(([k, v]) => k.match(/^levelOf/))
				.every(([k, v]) => typeof v === 'number')
		},
		get currentUserIsAdminOrCounsellor() {
			return AUTH.isStaff && AUTH.isCounsellor;
		},
		get targetModelTypeDisplayName() {
			return getAssignmentTargetModelTypeDisplayName(s.assignment?.targetType);
		},
		get associatedModelTypeDisplayName() {
			return getAssignmentAssociatedModelTypeDisplayName(s.assignment?.associatedType);
		},
		get afterTitle() {
			if (s.assignment && s.currentUserIsAdminOrCounsellor) {
				return () => <>
					<p>Generalised Anxiety Disorder Assessment</p>
					<p>For {s.associatedModelTypeDisplayName} #{s.assignment?.associatedId}</p>
				</>
			}
			else {
				return 'Generalised Anxiety Disorder Assessment'
			}
		},
		get currentUserCanManage() {
			return AUTH.can.provideCounsellingFor_.someUserGroups;
		},
		get readonly() {
			return p.readonly || Boolean(s.assignment?.timeCompleted);
		},
	}));

	useOnMount(action(() => {
		if (s.assignmentId) {
			s.survey.assignmentId = s.assignmentId;
		}
		if (s.assignment?.target) {
			s.survey.$patch(s.assignment.target as SurveyGAD7);
		}
	}));

	const submit = makeSubmitSurveyFn({
		API,
		UI,
		surveySubType: 'gad7',
		surveyModelName: ModelName.surveysGAD7,
		survey: s.survey,
		surveyDisplayName: 'survey',
		assignment: s.assignment,
		onComplete: survey => {
			if (s.assignment) {
				s.assignment.timeCompleted = getNowTimestampUtc();
				s.assignment.targetId = (survey as any).id;
			}
			p.onComplete && p.onComplete(s.assignment);
		}}
	);


	return <Observer children={() => (
		<AppPage className="OverlaySurveyGAD7">
			<AppPageHeader
				title="GAD7 Survey"
				afterTitle={s.afterTitle}
				endSlot={p.hideCloseButton ? null : <OverlayCloseButton onAfterClose={p.onCloseButton} />}
			/>
			<AppPageContent>
				<UIBlock padded spaceChildren>
					{
						!(p.survey || s.assignment?.target) && s.currentUserCanManage && <ShadedBlock spaceChildren color="orange">
							The user has not filled in the survey yet. You can fill it in for the user by completing the form below.
						</ShadedBlock>
					}
					<SurveyEditorGAD7
						survey={s.survey}
						readonly={s.readonly}
						showScore={!!s.assignment?.target || (!s.assignment?.target && s.currentUserCanManage)}
					/>
				</UIBlock>
				<AppPageContentFooter padded="all">
					{s.readonly || (
						<UIBlock>
							<BaseButton onClick={submit} disabled={!s.formIsComplete} fullWidth size="lg" dataCy="Submit-SurveyEditorGAD7">Submit</BaseButton>
						</UIBlock>
					)}
				</AppPageContentFooter>
			</AppPageContent>
		</AppPage>
	)} />
}

export default OverlaySurveyGAD7;